import _ from 'underscore'
import rpcCall from '../../util/rpc/rpcCalls'
import CustomerProjectSelection from "../../CustomerProjectSelection"
import {isAll, isOnlyCompletedType, isOnlyOpenType} from '../../workflowStatus/components/statusFilter/utils'
import ClassicBridge from '../../next/ClassicBridge'
import {arrayToSet} from '../../atUtils'
import {TaskSortBy} from "../constants";
import {mergeRpcAndNextTaskData} from "../../next/utils";
import {unsafe_selectWithState} from "../../redux/utils";
import {getWorkflowStatusesArray} from "../../entities/selectors";
import { CustomFieldsPrefix } from '../../customFields/constants'

const ID_LIMIT_IN_QUERY = 500

export const NEXT_SORTING_FIELDS = {
    CUSTOMER: "group.parent.title",
    PROJECT: "group.title",
    TASK: "title",
    STATUS: "active",
    WORKFLOW_STATUS: "workflowStatus",
    ESTIMATE: "estimate",
    DEADLINE: "deadline",
    LAST_TRACKING_DATE: "last_submitted_at",
    TYPE_OF_WORK: "typeOfWork.position",
    CREATION_DATE: "created_at",
    SPENT_TIME: "spentTime",
    VARIANCE_MAN_HOURS: "variance.duration",
    VARIANCE_PERCENTS: "variance.percentage",
    VARIANCE_PROGRESS_BAR: "progress",
    KANBAN_POSITION: "kanbanPosition",
}

const NEXT_SORTING_FIELDS_MAP = {
    [TaskSortBy.CUSTOMER]: NEXT_SORTING_FIELDS.CUSTOMER,
    [TaskSortBy.PROJECT]: NEXT_SORTING_FIELDS.PROJECT,
    [TaskSortBy.TASK]: NEXT_SORTING_FIELDS.TASK,
    [TaskSortBy.STATUS]: NEXT_SORTING_FIELDS.STATUS,
    [TaskSortBy.WORKFLOW_STATUS]: NEXT_SORTING_FIELDS.WORKFLOW_STATUS,
    [TaskSortBy.ESTIMATE]: NEXT_SORTING_FIELDS.ESTIMATE,
    [TaskSortBy.DEADLINE]: NEXT_SORTING_FIELDS.DEADLINE,
    [TaskSortBy.LAST_TRACKING_DATE]: NEXT_SORTING_FIELDS.LAST_TRACKING_DATE,
    [TaskSortBy.TYPE_OF_WORK]: NEXT_SORTING_FIELDS.TYPE_OF_WORK,
    [TaskSortBy.CREATION_DATE]: NEXT_SORTING_FIELDS.CREATION_DATE,
    [TaskSortBy.SPENT_TIME]: NEXT_SORTING_FIELDS.SPENT_TIME,
    [TaskSortBy.VARIANCE_MAN_HOURS]: NEXT_SORTING_FIELDS.VARIANCE_MAN_HOURS,
    [TaskSortBy.VARIANCE_PERCENTS]: NEXT_SORTING_FIELDS.VARIANCE_PERCENTS,
    [TaskSortBy.VARIANCE_PROGRESS_BAR]: NEXT_SORTING_FIELDS.VARIANCE_PROGRESS_BAR,
}

export function makeLoadTasksRequest({
    intersectedCPSelection,
    taskStatusFilter,
    customFieldsFilters,
    taskNameFilter,
    showOnlyManageableTasks,
    taskSorting,
    offset = 0,
    limit = null,
    noRetry
}) {
    const fillCommonNextDataParams = fillNextDataParams({
        intersectedCPSelection,
        taskStatusFilter,
        taskNameFilter,
        showOnlyManageableTasks
    }, {
        taskWorkflowStatuses: unsafe_selectWithState(getWorkflowStatusesArray)
    })

    const sortField = taskSorting.by.includes(CustomFieldsPrefix) ? taskSorting.by : NEXT_SORTING_FIELDS_MAP[taskSorting.by]

    const params = {
        sortField: sortField,
        ascSort:taskSorting.asc,
        offset,
        limit,
        customFieldsFilters,
    }
    fillCommonNextDataParams(params)
    
    const settings = {
        noRetry
    }

    return ClassicBridge.getTasksForTaskListSynced(params, settings).then((nextData) => loadAndMergeRpcAndNextData(nextData, intersectedCPSelection))
}

export function loadAndMergeRpcAndNextData(nextData, intersectedCPSelection) {
    const taskIds = _.pluck(nextData.items, "classicId")

    /* Load classic task entities with additional fields:
     * - creationTimestamp
     * - commentsNumber
     * - lasTTDate
     *
     * As well load additional page data
     */
    return rpcCall(
        "TaskManagementService.getTasks",
        [taskIds, intersectedCPSelection], // intersectedCPSelection is required for this request
        () => true
    ).then((rpcData) => mergeRpcAndNextData(rpcData, nextData))
}

function mergeRpcAndNextData(rpcData, nextData) {
    const taskIds = _.pluck(nextData.items, "classicId")

    // sort tasks by next data
    rpcData.tasks = _.sortBy(rpcData.tasks, (task) => {
        return taskIds.indexOf(task.id)
    })

    rpcData.tasks = mergeRpcAndNextTaskData(rpcData.tasks, nextData)
    return rpcData
}

function fillNextParamsWithCPSelection(params, cpSelection) {
    if (cpSelection.qualifier === CustomerProjectSelection.Qualifier.SELECTED) {
        if (cpSelection.customers.length !== 0)
            params.customerIds = cpSelection.customers
        if (cpSelection.projects.length !== 0)
            params.projectIds = cpSelection.projects
    }
    if (cpSelection.qualifier === CustomerProjectSelection.Qualifier.ALL_ACTIVE) {
        params.activeCPOnly = true
    } else {
        params.activeCPOnly = cpSelection.activeOnly
    }
}

export const fillNextDataParams = ({
    intersectedCPSelection,
    taskStatusFilter,
    taskNameFilter,
    showOnlyManageableTasks,
}, {
    taskWorkflowStatuses = [],
} = {}) => (params) => {
    intersectedCPSelection && fillNextParamsWithCPSelection(params, intersectedCPSelection)
    fillNextParamsWithTaskStatusFilter(params, taskStatusFilter, taskWorkflowStatuses)
    fillNextParamsWithTaskNameFilter(params, taskNameFilter)
    fillNextParamsWithOnlyManageableTasks(params, showOnlyManageableTasks)
}

function fillNextParamsWithTaskStatusFilter(params, taskStatusFilter, taskWorkflowStatuses = []) {
    if (isOnlyOpenType(taskStatusFilter))
        params.active = true
    else if (isOnlyCompletedType(taskStatusFilter))
        params.active = false
    else if (!isAll(taskStatusFilter)) {
        let selectedIdsSet = arrayToSet(taskStatusFilter.statusIds)
        params.workflowStatusIds = taskWorkflowStatuses
            .filter(status => {
                return !!selectedIdsSet[status.id] || taskStatusFilter.statusTypes.includes(status.type)
            }).map(status => status.id)
    }
}

export function fillNextParamsWithTaskNameFilter(params, taskNameFilter) {
    if (taskNameFilter)
        params.titleSearch = taskNameFilter
}

export function fillNextParamsWithOnlyManageableTasks(params, onlyManageableTasks) {
    if (onlyManageableTasks)
        params.onlyEditable = onlyManageableTasks
}



// WEBPACK FOOTER //
// ./web/js/taskManagement/tasksTable/connector.js